import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Phone, Mail, Users, MessageSquare, Clock, Edit, Trash } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { OrganizationWithRelations } from '@/types/custom-types';
import { interactionsService } from '@/services/relationship/interactionsService';
import { formatDateTime } from '@/lib/format';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ActivitiesTabProps {
  organization: OrganizationWithRelations;
  id: string;
}

const interactionIcons = {
  email: Mail,
  call: Phone,
  meeting: Users,
  message: MessageSquare,
  other: Calendar,
};

const interactionColors = {
  email: 'bg-blue-100 text-blue-800',
  call: 'bg-green-100 text-green-800',
  meeting: 'bg-purple-100 text-purple-800',
  message: 'bg-orange-100 text-orange-800',
  other: 'bg-gray-100 text-gray-800',
};

export function ActivitiesTab({ organization, id }: ActivitiesTabProps) {
  const [interactions, setInteractions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadInteractions();
  }, [id]);

  const loadInteractions = async () => {
    try {
      setLoading(true);
      const data = await interactionsService.getInteractionsByEntity('organization', id);
      setInteractions(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load activities',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await interactionsService.deleteInteraction(deleteId);
      toast({
        title: 'Success',
        description: 'Activity deleted successfully',
      });
      setDeleteId(null);
      loadInteractions();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete activity',
        variant: 'destructive',
      });
    }
  };

  const getInteractionIcon = (type: string) => {
    const Icon = interactionIcons[type as keyof typeof interactionIcons] || Calendar;
    return Icon;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Activities</CardTitle>
            <CardDescription>Recent interactions with {organization.name}</CardDescription>
          </div>
          <Button asChild size="sm">
            <Link to={`/activities/create?organization=${id}`}>
              <Calendar className="mr-2 h-4 w-4" />
              Log Activity
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Activities</CardTitle>
            <CardDescription>Recent interactions with {organization.name}</CardDescription>
          </div>
          <Button asChild size="sm">
            <Link to={`/activities/create?organization=${id}`}>
              <Calendar className="mr-2 h-4 w-4" />
              Log Activity
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {interactions.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
              <h3 className="mt-2 text-lg font-medium">No activities yet</h3>
              <p className="mt-1 text-muted-foreground">
                Log your first interaction with this organization
              </p>
              <Button asChild className="mt-4">
                <Link to={`/activities/create?organization=${id}`}>Log Activity</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {interactions.map((interaction) => {
                const Icon = getInteractionIcon(interaction.interaction_type);
                const colorClass =
                  interactionColors[
                    interaction.interaction_type as keyof typeof interactionColors
                  ] || interactionColors.other;

                return (
                  <div
                    key={interaction.id}
                    className="flex items-start gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className={`p-2 rounded-full ${colorClass}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium capitalize">
                              {interaction.interaction_type}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {interaction.direction}
                            </Badge>
                          </div>
                          {interaction.notes && (
                            <p className="text-sm text-muted-foreground mb-2">
                              {interaction.notes}
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDateTime(interaction.created_at)}
                            </div>
                            {interaction.duration && <span>{interaction.duration} minutes</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                            <Link to={`/activities/edit/${interaction.id}`}>
                              <Edit className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => setDeleteId(interaction.id)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Activity</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this activity? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
